import React from 'react';
import {
  AvatarCell,
  AvatarCellComponentProps,
  AvatarSkeleton,
  ChipsCell,
  ChipsCellComponentProps,
  ChipsSkeleton,
  Column,
  DefaultCellSkeleton,
  FilterItems,
} from '@odin-labs/components';
import { GetJobsiteWorkersOrderByField } from 'apollo/generated/client-operations';
import { Jobsite } from 'graphql/client/useAvailableJobsites';
import { getInitialsForUser, getUserFullName } from 'utils';
import { getPrettyFormattedDate } from 'utils/dates';
import { getDeveloperOptionsFromJobsites } from 'utils/filters';
import {
  Worker,
  Contractor,
  WorkersFilters,
  WorkerFiltersOptions,
} from 'containers/workers/types';
import { getContractorsOptions } from 'containers/contractor/helpers';
import { CalendarDayIcon, ShieldCheckIcon } from 'components/icons';
import { getTradeTitlePairsFromJobsiteWorkerEdges } from 'utils/worker';

export type WorkerColumn = Column<Worker>;

export const getColumns = (): WorkerColumn[] => [
  {
    id: 'worker',
    Header: 'Worker',
    accessor: (worker: Worker): string => getUserFullName(worker.user),
    Cell: AvatarCell,
    CellLoading: (): React.ReactElement => (
      <AvatarSkeleton size={['md', 'xl:xs']} />
    ),
    componentProps: (worker: Worker): AvatarCellComponentProps => {
      return {
        src: worker.profilePictureCropped?.downloadUrl,
        placeholder: getInitialsForUser(worker.user),
        objectFit: 'cover',
        size: ['md', 'xl:xs'],
        detail: getTradeTitlePairsFromJobsiteWorkerEdges(
          worker.jobsiteWorkers.edges,
        ),
      };
    },
  },
  {
    id: 'quickCode',
    Header: 'Quick Code',
    cellContainerClassName: '!odin-inline-block md:!odin-table-cell',
    accessor: 'quickCode',
    Cell: ({ value: quickCode }: { value: string }): React.ReactElement => (
      <span className="odin-text-sm odin-text-gray-700 md:odin-text-gray-900">
        <ShieldCheckIcon className="odin-text-odin-primary odin-mr-1 md:odin-hidden" />
        <span>{quickCode}</span>
      </span>
    ),
    CellLoading: (): React.ReactElement => (
      <DefaultCellSkeleton className="odin-w-24 md:odin-w-full" />
    ),
  },
  {
    id: 'dateOfBirth',
    Header: 'Date of Birth',
    cellContainerClassName: '!odin-inline-block !odin-pl-2 md:!odin-table-cell',
    accessor: (worker: Worker): string =>
      getPrettyFormattedDate(worker.birthDate),
    Cell: ({ value: dateOfBirth }: { value: string }): React.ReactElement => (
      <span className="odin-text-sm odin-text-gray-700 md:odin-text-gray-900">
        <CalendarDayIcon className="odin-text-odin-primary odin-mr-1 md:odin-hidden" />
        <span>{dateOfBirth}</span>
      </span>
    ),
    CellLoading: (): React.ReactElement => (
      <DefaultCellSkeleton className="odin-w-24 md:odin-w-full" />
    ),
  },
  {
    id: 'jobsiteAssignments',
    Header: 'Jobsite Assignments',
    Cell: ChipsCell,
    CellLoading: ChipsSkeleton,
    componentProps: (worker: Worker): ChipsCellComponentProps => ({
      chips: worker.jobsiteWorkers.edges.map(({ node }) => ({
        text: node.jobsiteContractor.jobsite.name,
        secondaryText: node.jobsiteContractor.contractor.organization.name,
        isActive: node.currentAccess.isAllowed,
      })),
    }),
    disableSortBy: true,
  },
];

export const getFilterItems = ({
  jobsites,
  contractors,
  developerIds,
  jobsiteIds,
  contractorIds,
  filtersOptions,
  trades,
  jobTitles,
  search,
}: {
  jobsites: Jobsite[];
  contractors: Contractor[];
  developerIds: string[];
  jobsiteIds: string[];
  contractorIds: string[];
  filtersOptions: WorkerFiltersOptions;
  trades: string[];
  jobTitles: string[];
  search: string;
}): FilterItems<WorkersFilters> => ({
  developerIds: {
    header: 'Client',
    type: 'dropdown',
    defaultValue: developerIds,
    componentProps: {
      options: getDeveloperOptionsFromJobsites(jobsites),
      allowSearch: false,
    },
  },
  jobsiteIds: {
    header: 'Jobsite',
    type: 'dropdown',
    defaultValue: jobsiteIds,
    componentProps: {
      options:
        jobsites?.map(({ jobsiteId: value, name: label }) => ({
          value,
          label,
        })) ?? [],
    },
  },
  contractorIds: {
    header: 'Contractor',
    type: 'dropdown',
    defaultValue: contractorIds,
    componentProps: {
      options: getContractorsOptions(contractors) ?? [],
      isVirtual: true,
    },
  },
  trades: {
    header: 'Trade',
    type: 'dropdown',
    defaultValue: trades,
    componentProps: { options: filtersOptions?.trades ?? [], isVirtual: true },
  },
  jobTitles: {
    header: 'Title',
    type: 'dropdown',
    defaultValue: jobTitles,
    componentProps: {
      options: filtersOptions?.jobTitles ?? [],
      isVirtual: true,
    },
  },
  search: {
    header: '',
    type: 'searchInput',
    defaultValue: search,
    isVisibleOutsideFilterOnMobile: true,
  },
});

export const orderByFields: Record<string, GetJobsiteWorkersOrderByField> = {
  name: GetJobsiteWorkersOrderByField.Name,
  jobsiteName: GetJobsiteWorkersOrderByField.JobsiteName,
  birthDate: GetJobsiteWorkersOrderByField.BirthDate,
  trade: GetJobsiteWorkersOrderByField.Trade,
  contractor: GetJobsiteWorkersOrderByField.Contractor,
  orientationDate: GetJobsiteWorkersOrderByField.OrientationDate,
  orientationStatus: GetJobsiteWorkersOrderByField.OrientationStatus,
  siteSpecificStatus: GetJobsiteWorkersOrderByField.SiteSpecificStatus,
};
